import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../../api/generated';
import { ILocationItem } from '../../../../../../components/custom/LocationTree';
import KendoLoading from '../../../../../../components/spinners/KendoLoading';
import { IWindowComponentProps } from '../../../../../../components/windows/WindowContext';
import { locationType, waitingTimesWarningType } from '../../../../../../enums';
import { IServiceListItem } from '../../../../../../models/classes/IService';
import { mapLocationsToOptions } from '../../../../../../utils';
import { IUserWaitingTime } from '../../GeneralSettingsPage';

interface UserWarningOfWaitingTimesCreateWindowProps {
    organizationInformationId: number;
    fetchData(): Promise<void>;
}

const validationSchema = Yup.object<IUserWaitingTime>().shape({
    email: Yup.string().required('Šis laukas yra privalomas').email('Neteisingas el. pašto formatas'),
    patientAttendanceTime: Yup.number().required('Šis laukas yra privalomas'),
    patientWaitingTime: Yup.number().required('Šis laukas yra privalomas')
});

const UserWarningOfWaitingTimesCreateWindow: FC<IWindowComponentProps<UserWarningOfWaitingTimesCreateWindowProps>> = (props) => {
    const {
        componentProps: { fetchData, organizationInformationId },
        onClose
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [services, setServices] = useState<IServiceListItem[] | null>(null);
    const [departmentLocations, setDepartmentLocations] = useState<ILocationItem[] | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            const [servicesResp, departmentLocationsResp] = await Promise.all([
                api.service.getServices({ organizationInformationId: organizationInformationId, skip: 0, take: 20 }),
                api.administration.getLocations(locationType.Department)
            ]);
            setServices(servicesResp?.data!);
            setDepartmentLocations(departmentLocationsResp);
        };

        fetchData();
    }, []);

    const mapServicesToOptions = (services: IServiceListItem[]): fabric.IDropdownOption[] => {
        return services.map((x) => {
            return {
                key: x.id,
                text: x.name_LT ?? ''
            };
        });
    };

    const handleSubmit = async (values: IUserWaitingTime, formikActions: formik.FormikHelpers<IUserWaitingTime>) => {
        try {
            await api.administration.createUserWarningOfWaitingTime(values);
            toast.success('Įrašas sėkmingai sukurtas');

            await fetchData();
            onClose();
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    {services && departmentLocations && (
                        <div className="ms-Grid" dir="ltr">
                            <div style={{ padding: '20px' }} className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                                <formik.Formik
                                    initialValues={
                                        {
                                            waitingTimesWarningType: 0,
                                            serviceId: services.length > 0 ? services[0].id : null,
                                            locationId: departmentLocations.length > 0 ? departmentLocations[0].id : null
                                        } as IUserWaitingTime
                                    }
                                    onSubmit={handleSubmit}
                                    validationSchema={validationSchema}
                                    render={(formikProps) => (
                                        <formik.Form>
                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                                                    <formik.Field
                                                        name="email"
                                                        render={({ field, form }: formik.FieldProps<string>) => (
                                                            <fabric.TextField
                                                                {...field}
                                                                label="El. paštas"
                                                                placeholder="Įrašykite el. paštą"
                                                                errorMessage={
                                                                    (formikProps.status && formikProps.status.email) ||
                                                                    (form.errors.email && form.errors.email)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                    <formik.Field
                                                        name="waitingTimesWarningType"
                                                        render={({ field, form }: formik.FieldProps<string>) => (
                                                            <fabric.Dropdown
                                                                {...field}
                                                                label="Įspėjimo tipas"
                                                                options={waitingTimesWarningType.displayArray()}
                                                                selectedKey={field.value}
                                                                onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                    {formikProps.values.waitingTimesWarningType === waitingTimesWarningType.WarningForService ? (
                                                        <formik.Field
                                                            name="serviceId"
                                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                                <fabric.Dropdown
                                                                    {...field}
                                                                    label="Paslauga"
                                                                    options={mapServicesToOptions(services)}
                                                                    placeholder="Nėra sukurtų paslaugų"
                                                                    selectedKey={field.value}
                                                                    defaultSelectedKey={form.values.serviceId}
                                                                    onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                                />
                                                            )}
                                                        />
                                                    ) : (
                                                        <formik.Field
                                                            name="locationId"
                                                            render={({ field, form }: formik.FieldProps<string>) => (
                                                                <fabric.Dropdown
                                                                    {...field}
                                                                    label="Padalinys"
                                                                    options={mapLocationsToOptions(departmentLocations, false)}
                                                                    placeholder="Nėra sukurtų padalinių"
                                                                    selectedKey={field.value}
                                                                    defaultSelectedKey={form.values.locationId}
                                                                    onChange={({}, item) => form.setFieldValue(field.name, item && item.key)}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="ms-Grid-row">
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                    <formik.Field
                                                        name="patientWaitingTime"
                                                        render={({ field, form }: formik.FieldProps<string>) => (
                                                            <fabric.TextField
                                                                type="number"
                                                                {...field}
                                                                label="Paciento laukimo laikas"
                                                                placeholder="Įrašykite paciento laukimo laiką minutėmis"
                                                                errorMessage={
                                                                    (formikProps.status && formikProps.status.patientWaitingTime) ||
                                                                    (form.errors.patientWaitingTime && form.errors.patientWaitingTime)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                                    <formik.Field
                                                        name="patientAttendanceTime"
                                                        render={({ field, form }: formik.FieldProps<string>) => (
                                                            <fabric.TextField
                                                                {...field}
                                                                type="number"
                                                                label="Paciento aptarnavimo laikas"
                                                                placeholder="Įrašykite paciento aptarnavimo laiką minutėmis"
                                                                errorMessage={
                                                                    (formikProps.status && formikProps.status.patientAttendanceTime) ||
                                                                    (form.errors.patientAttendanceTime && form.errors.patientAttendanceTime)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                                <fabric.PrimaryButton
                                                    className="ms-slideRightIn40 ms-textAlignRight"
                                                    text="Išsaugoti"
                                                    disabled={
                                                        formikProps.isSubmitting ||
                                                        !formikProps.isValid ||
                                                        !formikProps.dirty ||
                                                        (formikProps.values.serviceId === null && formikProps.values.locationId === null)
                                                    }
                                                    type="submit"
                                                />
                                            </div>
                                        </formik.Form>
                                    )}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default UserWarningOfWaitingTimesCreateWindow;
